// FIN-865: per-browser-session correlation id plus a ring buffer of the last 10 minted
// correlation ids. Exported for FIN-550's feedback capture (requirements.md F6: "trace: session
// id, current correlation id, last N correlation ids") — this file has no feedback-specific
// logic itself, it's just where that trace state already lives once correlatedFetch.ts starts
// minting ids.
//
// Session id persists for the browser tab's sessionStorage lifetime (survives a reload, not a
// new tab); correlation ids are per-request and kept in memory only, never persisted.
const SESSION_STORAGE_KEY = 'harmony-session-id';
const RING_BUFFER_SIZE = 10;

let sessionId: string | undefined;
const recentCorrelationIds: string[] = [];

function readSessionStorage(): Storage | undefined {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : undefined;
  } catch {
    // Blocked storage (some private-browsing modes throw on access, not just on write).
    return undefined;
  }
}

/** Lazily creates — and persists in sessionStorage — one id for the life of this browser tab. */
export function getSessionId(): string {
  if (sessionId) return sessionId;
  const storage = readSessionStorage();
  try {
    const existing = storage?.getItem(SESSION_STORAGE_KEY);
    if (existing) {
      sessionId = existing;
      return sessionId;
    }
  } catch {
    // Fall through to an in-memory-only id — a request must never fail because
    // sessionStorage.getItem() threw.
  }
  sessionId = crypto.randomUUID();
  try {
    storage?.setItem(SESSION_STORAGE_KEY, sessionId);
  } catch {
    // Best-effort persistence only — the in-memory id above is still usable this tab-life.
  }
  return sessionId;
}

/** Records a newly-minted correlation id, keeping only the most recent RING_BUFFER_SIZE. */
export function recordCorrelationId(id: string): void {
  recentCorrelationIds.push(id);
  if (recentCorrelationIds.length > RING_BUFFER_SIZE) {
    recentCorrelationIds.shift();
  }
}

/** The last (up to) 10 correlation ids minted this session, oldest first. */
export function getRecentCorrelationIds(): readonly string[] {
  return [...recentCorrelationIds];
}

/** The most recently minted id, or undefined before any request has gone out this session. */
export function getLastCorrelationId(): string | undefined {
  return recentCorrelationIds[recentCorrelationIds.length - 1];
}

/** Test-only reset — mirrors similar resets elsewhere in this codebase (e.g. rateLimiter.ts). */
export function __resetCorrelationStoreForTests(): void {
  sessionId = undefined;
  recentCorrelationIds.length = 0;
}
